import * as CONSTANTS from "./constants";
import {
    loading_sports_schedule,
    receive_rightRail_schedule,
    receive_main_sports_schedule,
    update_sports_filter,
    update_sports_drop_down_list,
    update_sports_filter_bar,
} from "../../reducers/Schedule/reducer";
import log from "../../utils/logger/library";
import { request } from "../../utils/promises/library";
import { set_loading_complete } from "../Loader/dispatchers";
import { start_loading } from "../../reducers/Loader/reducer";
import { generateRadioSchedule, generateSportsSchedule } from "./records";
import { isResponseValid } from "../../utils/headers/library";
import { SCHEDULE_DATA_CACHED_TIME } from "../../components/Schedule/components/MainSchedule/constants";
import { fetch_broadcast_schedule } from "../../fetch/other/broadcast_schedule/library";
import { transformScheduleList } from "../../utils/dataTransform/library";
import { createCaption } from "../../utils/schedule/format";
import { createDropDownList } from "../../utils/schedule/utils";
import { createFilteredScheduleList } from "../../utils/schedule/library";

/**
 * Turn the json into a record instance.
 *
 * @param {string} type The type for which we got the schedule, e.g. "radio", "sports"
 * @param {object} scheduleEntry The single schedule entry
 * @returns {object} The SportsSchedule object
 */
const createScheduleRecord = (type = "sports", scheduleEntry) => {
    switch (type) {
        // If we received the Radio schedule
        case "radio":
            if (!scheduleEntry) {
                return generateRadioSchedule();
            }
            return generateRadioSchedule({
                startDate: scheduleEntry.startDate,
                endDate: scheduleEntry.endDate,
                startTime: scheduleEntry.startTime,
                endTime: scheduleEntry.endTime,
                epochStart: scheduleEntry.epochStart,
                epochEnd: scheduleEntry.epochEnd,
                title: scheduleEntry.program ? scheduleEntry.program.title : "",
                url: scheduleEntry.program ? scheduleEntry.program.website : "",
                networkName: scheduleEntry.networkName,
            });

        // If we received the Sports schedule
        case "sports":
            if (!scheduleEntry) {
                return generateSportsSchedule();
            }

            return generateSportsSchedule({
                title: scheduleEntry.ti || "",
                types: scheduleEntry.typ || [],
                url: scheduleEntry.url || "",
                thumbnail: scheduleEntry.thumb || "",
                media: scheduleEntry.on || [],
                startTime: scheduleEntry.stt || "",
                endTime: scheduleEntry.end || "",
            });
        default:
            return generateSportsSchedule();
    }
};

/**
 * Either make an API call to fetch the requested schedule type (e.g. radio or sports), or get it from the state
 * @param {string} type The name of the selected type
 * @param {string} regionName Optional region name, if Radio broadcast was requested
 * @return {void}
 */
export function fetch_schedule(type, regionName = "") {
    return (dispatch, getState) => {
        const state = getState();

        // Check if the current schedule type is already in the state
        const sectionSchedule = state.schedule ? state.schedule[type] : null;
        const currentSchedule =
            sectionSchedule && sectionSchedule.scheduleList
                ? sectionSchedule
                : null;
        const isRegionalSchedule = currentSchedule
            ? currentSchedule.regionName
            : null;

        if (
            !currentSchedule ||
            (isRegionalSchedule && currentSchedule.regionName !== regionName)
        ) {
            makeScheduleApiCall(dispatch, type, regionName);
        }
    };
}

/**
 * @param {object} dispatch The dispatching function
 * @param {string} type The type of schedule to be fetched, e.g. "radio", "sports"
 * @param {string} regionName Optional region name, if Radio broadcast was requested
 * @returns {void}
 */
export function makeScheduleApiCall(dispatch, type = "", regionName = "") {
    dispatch(start_loading());

    let apiURL = type ? CONSTANTS.API[type] : CONSTANTS.API.default;
    if (regionName) {
        apiURL = `${apiURL}${encodeURIComponent(`&locationKey=${regionName}`)}`;
    }

    request(apiURL)
        .then((response) => {
            if (isResponseValid(response)) {
                return response.json();
            }
        })
        .then((response) => {
            const items = response.schedule
                ? response.schedule
                : response.timeslots;
            if (items && items.length > 0) {
                const timestamp = Date.now();
                const formattedItems = [];
                items.forEach((item) => {
                    formattedItems.push(createScheduleRecord(type, item));
                });
                // Dispatch a state update
                dispatch(
                    receive_rightRail_schedule({
                        scheduleList: formattedItems,
                        type,
                        timestamp,
                        regionName,
                    })
                );
            }
            dispatch(set_loading_complete());
        })

        .catch((error) => {
            log.error(`(makeScheduleApiCall) ${type} request failed`, error);
            dispatch(set_loading_complete());
        });
}

export const fetchSportsSchedule = (key, isServer) => (dispatch, getState) => {
    const { schedule } = getState();

    if (
        Date.now() - (schedule?.sportsSchedule?.timestamp || 0) >
        SCHEDULE_DATA_CACHED_TIME
    ) {
        dispatch(start_loading());
        dispatch(
            loading_sports_schedule({
                loading: true,
            })
        );

        const fetchBroadcastSchedulepromise = fetch_broadcast_schedule()
            .then((json) => {
                const scheduleList = transformScheduleList(json.schedule);
                const keyState = schedule[key] || {};
                const filteredScheduleList = createFilteredScheduleList(
                    keyState?.filterBar,
                    keyState?.filter,
                    scheduleList,
                    keyState?.zone
                );
                const caption = createCaption(
                    keyState?.filter,
                    keyState?.filterBar
                );
                dispatch(
                    receive_main_sports_schedule({
                        key,
                        sportsSchedule: {
                            timestamp: Date.now(),
                            loading: false,
                            scheduleList,
                        },
                        filteredScheduleList,
                        caption,
                    })
                );
            })
            .catch((error) => {
                dispatch(
                    loading_sports_schedule({
                        loading: false,
                    })
                );
                log.error(
                    "(fetchSportsSchedule) fetch_broadcast_schedule",
                    error
                );
            })
            .finally(() => {
                dispatch(set_loading_complete());
            });
        // Suspend the component to wait for the data fetching process to be done
        if (isServer) throw fetchBroadcastSchedulepromise;
        return fetchBroadcastSchedulepromise;
    }
    return Promise.resolve();
};

export const update_sports_schedule_filter_bar =
    (key, filterBar) => (dispatch, getState) => {
        const { schedule } = getState();
        const scheduleList = schedule?.sportsSchedule?.scheduleList;
        const keyState = schedule[key] || {};
        const fb = {
            ...keyState?.filterBar,
            ...filterBar,
        };
        const filteredScheduleList = createFilteredScheduleList(
            fb,
            keyState?.filter,
            scheduleList,
            keyState?.zone
        );
        const caption = createCaption(keyState?.filter, filterBar);

        dispatch(
            update_sports_filter_bar({
                key,
                filterBar: fb,
                filteredScheduleList,
                caption,
            })
        );
    };

export const update_sports_schedule_filter =
    (key, filter, zone) => (dispatch, getState) => {
        const { schedule } = getState();
        const scheduleList = schedule?.sportsSchedule?.scheduleList;
        const keyState = schedule[key] || {};
        const filteredScheduleList = createFilteredScheduleList(
            keyState?.filterBar,
            filter,
            scheduleList,
            zone
        );
        const caption = createCaption(filter, keyState?.filterBar);
        const dropDownList = filter?.types
            ? createDropDownList(filter.types)
            : {};

        dispatch(
            update_sports_filter({
                filter,
                key,
                zone,
                filteredScheduleList,
                caption,
                dropDownList,
            })
        );
    };

export const update_sports_schedule_drop_down_list =
    (key) => (dispatch, getState) => {
        const { schedule } = getState();
        const keyState = schedule[key] || {};
        const dropDownList = createDropDownList(keyState?.filter?.types);

        dispatch(
            update_sports_drop_down_list({
                key,
                dropDownList,
            })
        );
    };
